import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { v4 as uuid } from 'uuid';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import agent from '../../app/api/agent';
import { MetaDataElement } from '../../app/models/metaDataConfig';
import FormComponentDyDb from '../DynamicDashboard/form/FormComponent';
import { ThreeDotLoader } from '../../app/layout/LoadingComponent';
import { useStore } from '../../app/stores/store';
import sendRespectiveEmail from '../../app/models/EmailNew';
import QRCodeCanvas from 'qrcode.react';
import './styles.css';
import { commSettingsForDynDash } from '../../app/stores/CommunicationSettingForDynamicDashboard/service';

type masterElementType = {
  elementId: string;
  elementName: string;
  mode: number | undefined;
  criteria?: { type: string; value: string } | undefined;
  dependency?: {
    basedOn: string;
    checkWith: string;
  };
};

function FormRegister({
  masterId,
  isEdit,
  assetId,
  masterName,
  afterSavedHandler,
  metaData,
  cancelBtnMode,
  submitBtnMode,
  automaticPdf = false,
  setIsTableUpdated,
  dataGridName,
}: {
  masterId?: string;
  isEdit?: boolean;
  assetId?: string;
  masterName?: string;
  afterSavedHandler?: () => void;
  metaData: any[];
  cancelBtnMode?: number;
  submitBtnMode?: number;
  automaticPdf: boolean;
  setIsTableUpdated: any;
  dataGridName?: string;
}) {
  const { id } = useParams<{ id: string }>();

  const {
    DynamicDashboardStore,
    modalStore,
    userStore,
    commonStore,
    DashboardConfigurationStore,
  } = useStore();

  const { user, userList, loadUsers } = userStore;

  const [formDetails, setFormDetails] = useState<MetaDataElement[]>([]);
  const [formName, setFormName] = useState('');
  const [card, setCard] = useState<string>('');
  const defaultForm = useRef([]);
  const qrRef = useRef<HTMLDivElement>(null);
  const [loader, setLoader] = useState({
    content: false,
    submit: false,
  });

  useEffect(() => {
    if (userList.length === 0) loadUsers();
  }, [userList.length, loadUsers]);

  const getForm = async () => {
    setLoader((p) => ({
      ...p,
      content: true,
    }));

    if (isEdit) {
      let fResult = await agent.newMasterReport.getMasterAsset({
        assetId: assetId || '',
      });
      if (fResult?.data) {
        defaultForm.current = fResult?.data?.components;
        // setFormDetails(fResult?.data?.components);
        let tempFormDetails: MetaDataElement[] = [];

        metaData?.forEach((metaDt: masterElementType) => {
          fResult?.data?.components?.forEach((result: MetaDataElement) => {
            if (metaDt?.elementId === result?.templateElementId) {
              let temp = {
                ...result,
                mode: metaDt?.mode !== undefined ? metaDt?.mode : 1,
                dependency: metaDt.dependency,
              };
              tempFormDetails.push(temp);
            }
          });
        });
        setFormDetails(
          tempFormDetails.length > 0
            ? tempFormDetails
            : fResult?.data?.components,
        );
      }
    } else {
      let [fResult, SResult] = await Promise.all([
        agent.newMasterReport.getMetadataList({
          masterId: id || '',
        }),
        agent.newMasterReport.getMasterName({
          masterId: id || '',
        }),
      ]);
      if (fResult?.data) {
        defaultForm.current = fResult?.data;
        setFormDetails(fResult?.data);
        setFormName(SResult?.data);
      }
    }
    setLoader((p) => ({
      ...p,
      content: false,
    }));
  };

  useEffect(() => {
    getForm();
    // eslint-disable-next-line
  }, [id]);

  const downloadPDF = async (
    updatedFormDetails: any[],
    headerImage?: string,
  ) => {
    const unit = 'pt';
    const size = 'A4'; // Use A1, A2, A3 or A4
    const orientation = 'portrait'; // portrait or landscape

    const marginLeft = 40;
    const doc = new jsPDF(orientation, unit, size);

    doc.setFontSize(15);

    // const title = 'Delivery Report';
    const headers = [['Title', 'Values']];

    const data: any[][] = [];
    const imageURL = headerImage ? headerImage : '/assets/logo.png';
    doc.addImage(imageURL, 'PNG', marginLeft, 40, 500, 80);

    data.push(['Serial No.', assetId]);
    let canvas = qrRef?.current?.querySelector('canvas');
    let image = canvas?.toDataURL('image/png') || '';

    doc.addImage(image, 'png', marginLeft, 140, 40, 40);

    metaData?.forEach((metDt) => {
      updatedFormDetails.forEach((dataFromAPI) => {
        if (metDt.elementId === dataFromAPI.templateElementId) {
          if (metDt.mode !== 2) {
            let values = [];
            let splitValue;
            if (dataFromAPI.elementValue?.includes('DynamicTable|')) {
              const stringData =
                dataFromAPI.elementValue?.split('DynamicTable|')?.[1];
              let tempValue =
                stringData !== undefined ? JSON.parse(stringData) : [];
              const rowsList: string[] = [];
              let columnHeaders: string[] = Object.keys(tempValue[0]);
              tempValue?.forEach((val: any) => {
                let rowVal = '';
                columnHeaders?.forEach((header: string, index: number) => {
                  if (header !== '_uuid_') {
                    rowVal += header?.split('_')?.join(' ');
                    rowVal += ': ';
                    rowVal += val?.[header];
                    if (index !== columnHeaders.length - 1) {
                      rowVal += ', ';
                    }
                  }
                });
                rowsList.push(rowVal);
              });
              splitValue = rowsList.join('\n');
              values.push(
                dataFromAPI.elementName,
                splitValue === undefined
                  ? dataFromAPI.elementValue
                  : splitValue,
              );
            } else {
              splitValue = dataFromAPI.elementValue?.split('|');
              values.push(
                dataFromAPI.elementName,
                splitValue?.[1] === undefined
                  ? dataFromAPI.elementValue
                  : splitValue[1],
              );
            }
            data.push(values);
          }
        }
      });
    });

    data.push(['TimeStamp', new Date()]);

    let content = {
      startY: 200,
      head: headers,
      body: data,
    };

    // doc.text(title, marginLeft, 40);
    autoTable(doc, content);
    doc.save('Report.pdf');

    // const response = await agent.Files.uploadFile(doc.output('blob') as File);
  };

  // const exportPDF = (updatedFormDetails: any[]) => {
  //   const unit = 'pt';
  //   const size = 'A4'; // Use A1, A2, A3 or A4
  //   const orientation = 'portrait'; // portrait or landscape

  //   const marginLeft = 40;
  //   const doc = new jsPDF(orientation, unit, size);

  //   doc.setFontSize(15);

  //   // const title = 'Delivery Report';
  //   const headers = [['Title', 'Values']];

  //   const data: any[][] = [];
  //   const imageURL = '/assets/LVPCMS.png';
  //   doc.addImage(imageURL, 'JPEG', marginLeft, 40, 500, 80);

  //   updatedFormDetails.forEach((d) => {
  //     let values = [];
  //     let splitValue = d.elementValue?.split('|');
  //     if (
  //       d.elementName !== 'Returned' &&
  //       d.elementName !== 'Moved to Store' &&
  //       d.elementName !== 'Photograph of Returned Asset' &&
  //       d.elementName !== 'Photograph of Asset Moving to Store' &&
  //       d.elementName !== 'Asset Checker 1' &&
  //       d.elementName !== 'Asset Checker 2' &&
  //       d.elementName !== 'Asset Damaged ?'
  //     ) {
  //       if (DynamicDashboardStore.staticSelectedCard === 'Registrar Approval') {
  //         if (
  //           d.elementName !== 'Scan Security' &&
  //           d.elementName !== 'Security Name'
  //         ) {
  //           values.push(
  //             d.elementName,
  //             splitValue?.[1] === undefined ? d.elementValue : splitValue[1],
  //           );
  //           data.push(values);
  //         }
  //       } else {
  //         values.push(
  //           d.elementName,
  //           splitValue?.[1] === undefined ? d.elementValue : splitValue[1],
  //         );
  //         data.push(values);
  //       }
  //     }
  //   });

  //   if (DynamicDashboardStore.staticSelectedCard === 'Ready to Deliver') {
  //     data.push(['Ready to Deliver', new Date()]);
  //   }

  //   let content = {
  //     startY: 150,
  //     head: headers,
  //     body: data,
  //   };

  //   // doc.text(title, marginLeft, 40);
  //   autoTable(doc, content);
  //   doc.save('Delivery_Report.pdf');
  // };

  const updateAssetInVault = async (itemIds: string[], status: string) => {
    if (itemIds.length !== 0) {
      const payload = {
        itemIds,
        assetInVault: status,
      };
      await agent.Activities.AlterAssetInVault(payload);
    }
  };

  useEffect(() => {
    if (
      DynamicDashboardStore.staticSelectedCard &&
      commonStore.staticDemoChanges
    ) {
      setCard(DynamicDashboardStore.staticSelectedCard);
    }
    // eslint-disable-next-line
  }, [DynamicDashboardStore.staticSelectedCard]);

  const getMailBody = (
    userName: string,
    masterName: string,
    approved: boolean,
    approver: string,
    details?: string,
  ) => {
    if (approved) {
      return `Dear ${userName},<br/>\u00A0 This is to notify you that the ${masterName} has been successfully approved by ${approver}. <br/> <br/> ${details} <br/> Regards, <br/> info@honeycombmanager.com`;
    } else {
      return `Dear ${userName},<br/>\u00A0 This is to notify you that the ${masterName} has been denied by ${approver}. <br/> <br/> ${details} <br/> Regards, <br/> info@honeycombmanager.com
`;
    }
  };

  const saveHandler = async (updatedFormDetails: any[]) => {
    setLoader((p) => ({
      ...p,
      submit: true,
    }));
    let errorMsg = `scanned barcode(s) doesn't match with selected asset(s)`;
    let result;
    let status = ``;
    let assetIdLS = assetId || uuid();
    let callApi = true;

    try {
      if (isEdit) {
        let items: string[] = [];
        updatedFormDetails?.forEach((data) => {
          status += `***${data.elementName}*** : ~~~${data.elementValue}~~~ \n`;
          if (
            commonStore.staticDemoChanges &&
            data.elementName === 'HOD Approval' &&
            data.elementValue === 'Denied'
          ) {
            // items = data.elementValue
            //   ?.split('|')?.[1]
            //   ?.split(',')
            //   .flatMap((d: string) => d || []);
            callApi = false;
          }
        });
        // let itemAccessPayload = {
        //   userId: userStore.user?.id,
        //   itemsList: items,
        // };
        let payload = {
          assetId: assetIdLS,
          components: updatedFormDetails,
        };
        let response;
        if (commonStore.staticDemoChanges && callApi) {
          response = await agent.Activities.ItemAccessPermit(payload);
        }
        if (
          commonStore.staticDemoChanges &&
          card === 'Rental Approval By CEO'
        ) {
          await agent.AssetRental.masterAssetRentalApproval(
            masterId || '',
            payload,
          );
        }
        if (
          commonStore.staticDemoChanges &&
          !response?.data?.includes('Okay') &&
          callApi
        ) {
          toast.info(response?.data);
        } else {
          if (automaticPdf) {
            if (commonStore.staticDemoChanges) {
              downloadPDF(updatedFormDetails, '/assets/LVPCMS.png');
            } else {
              downloadPDF(updatedFormDetails);
            }
          }
          result = await agent.newMasterReport.updateMasterAsset(payload);

          if (result.data?.includes(errorMsg)) {
            toast.warning(result.data);
          } else if (
            result.data === `Asset not selected to scan` ||
            result.data?.includes('barcode(s) is not scanned')
          ) {
            toast.warning(result.data);
          } else if (result.data.includes(`doesn't match`)) {
            toast.warning(result.data);
          } else {
            toast.success('Asset Updated Successfully');
            afterSavedHandler?.();
            setIsTableUpdated?.(true);
            if (commonStore.staticDemoChanges) {
              let mailBody = '';
              let details = '';
              let username =
                updatedFormDetails.flatMap((data) =>
                  data.elementName === 'Student Name' ? data.elementValue : [],
                )?.[0] || '';
              let email = updatedFormDetails.flatMap((data) =>
                data.elementName === 'Student Email' ? data.elementValue : [],
              )?.[0];
              if (
                card === 'Rental Approval By Registrar' ||
                card === 'Rental Approval By CEO'
              ) {
                username =
                  updatedFormDetails.flatMap((data) =>
                    data.elementName === 'Request Raised By'
                      ? data.elementValue
                      : [],
                  )?.[0] || '';
                email = updatedFormDetails.flatMap((data) =>
                  data.elementName === 'Request Raised From (Mail ID)'
                    ? data.elementValue
                    : [],
                )?.[0];
                updatedFormDetails.forEach((data) => {
                  if (data.mode !== 2) {
                    if (data.elementType === 'Masters') {
                      details +=
                        data.elementName +
                        ': ' +
                        data.elementValue?.split('|')?.[1] +
                        '<br />';
                    } else {
                      details +=
                        data.elementName + ': ' + data.elementValue + '<br />';
                    }
                  }
                });
              }
              if (card === 'Rental Approval By Registrar') {
                if (
                  updatedFormDetails.flatMap((data) =>
                    data.elementName === 'Registrar Approval'
                      ? data.elementValue
                      : [],
                  )?.[0] === 'Approved'
                ) {
                  mailBody = getMailBody(
                    username,
                    'Asset Rental Request',
                    true,
                    'Registrar',
                    details,
                  );
                } else if (
                  updatedFormDetails.flatMap((data) =>
                    data.elementName === 'Registrar Approval'
                      ? data.elementValue
                      : [],
                  )?.[0] === 'Denied'
                ) {
                  mailBody = getMailBody(
                    username,
                    'Asset Rental Request',
                    false,
                    'Registrar',
                    details,
                  );
                }
              } else if (card === 'Rental Approval By CEO') {
                if (
                  updatedFormDetails.flatMap((data) =>
                    data.elementName === 'CEO Approval'
                      ? data.elementValue
                      : [],
                  )?.[0] === 'Approved'
                ) {
                  mailBody = getMailBody(
                    username,
                    'Asset Rental Request',
                    true,
                    'CEO',
                    details,
                  );
                } else if (
                  updatedFormDetails.flatMap((data) =>
                    data.elementName === 'CEO Approval'
                      ? data.elementValue
                      : [],
                  )?.[0] === 'Denied'
                ) {
                  mailBody = getMailBody(
                    username,
                    'Asset Rental Request',
                    false,
                    'CEO',
                    details,
                  );
                }
              }
              if (
                card === 'Rental Approval By Registrar' ||
                card === 'Rental Approval By CEO'
              ) {
                sendRespectiveEmail(
                  '',
                  '',
                  // masterName || '',
                  `Asset Rental Request - Status update`,
                  '',
                  '',
                  email,
                  mailBody,
                );
              }
              if (card === 'Approval') {
                if (
                  updatedFormDetails.flatMap((data) =>
                    data.elementName === 'HOD Approval'
                      ? data.elementValue
                      : [],
                  )?.[0] === 'Approved'
                ) {
                  mailBody = getMailBody(
                    username,
                    masterName || 'Asset Outward Request',
                    true,
                    'HOD',
                  );
                } else if (
                  updatedFormDetails.flatMap((data) =>
                    data.elementName === 'HOD Approval'
                      ? data.elementValue
                      : [],
                  )?.[0] === 'Denied'
                ) {
                  mailBody = getMailBody(
                    username,
                    masterName || 'Asset Outward Request',
                    false,
                    'HOD',
                  );
                }
                if (DynamicDashboardStore.assetChangedByHOD) {
                  let changedItems = '';
                  for (var i = 0; i < items.length; i++) {
                    changedItems = changedItems + ', ' + items[i];
                  }
                  sendRespectiveEmail(
                    '',
                    '',
                    // masterName || '',
                    `${masterName} - Status update`,
                    '',
                    '',
                    email,
                    `Dear ${username},<br/>\u00A0 This is to notify that in the ${masterName} raised by you, some or all of the assets have been altered by the HOD. Please find the list: <br/> ${changedItems} <br/> <br/> Regards, <br/> info@honeycombmanager.com
                  `,
                  );
                }
                if (!DynamicDashboardStore.assetChangedByHOD) {
                  sendRespectiveEmail(
                    '',
                    '',
                    // masterName || '',
                    `${masterName} - Status update`,
                    '',
                    '',
                    email,
                    mailBody,
                  );
                }
              } else if (card === 'Prepare to Deliver') {
                sendRespectiveEmail(
                  '',
                  '',
                  // masterName || '',
                  `${masterName} - Status update`,
                  '',
                  '',
                  email,
                  `Dear ${username},<br/>\u00A0 This is to notify that the asset(s) you had requested is being prepared for delivery. <br/> <br/> Regards, <br/> info@honeycombmanager.com
                  `,
                );
              } else if (card === 'Registrar Approval') {
                if (
                  updatedFormDetails.flatMap((data) =>
                    data.elementName === 'Registrar Approval'
                      ? data.elementValue
                      : [],
                  )?.[0] === 'Approved'
                ) {
                  mailBody = getMailBody(
                    username,
                    masterName || 'Asset Outward Request',
                    true,
                    'Registrar',
                  );
                } else {
                  mailBody = getMailBody(
                    username,
                    masterName || 'Asset Outward Request',
                    false,
                    'Registrar',
                  );
                }
                sendRespectiveEmail(
                  '',
                  '',
                  // masterName || '',
                  `${masterName} - Status update`,
                  '',
                  '',
                  email,
                  mailBody,
                );
              } else if (card === 'Ready to Deliver') {
                sendRespectiveEmail(
                  '',
                  '',
                  // masterName || '',
                  `${masterName} - Status update`,
                  '',
                  '',
                  email,
                  `Dear ${username},<br/>\u00A0 This is to notify that the asset(s) you had requested is ready for delivery. <br/> <br/> Regards, <br/> info@honeycombmanager.com
                  `,
                );
              } else if (card === 'Return') {
                sendRespectiveEmail(
                  '',
                  '',
                  // masterName || '',
                  `${masterName} - Status update`,
                  '',
                  '',
                  email,
                  `Dear ${username},<br/>\u00A0 This is to notify that the asset(s) are returned successfully. <br/> <br/> Regards, <br/> info@honeycombmanager.com
                  `,
                );
              } else if (card === 'Move to Store') {
                sendRespectiveEmail(
                  '',
                  '',
                  // masterName || '',
                  `${masterName} - Status update`,
                  '',
                  '',
                  email,
                  `Dear ${username},<br/>\u00A0 This is to notify that the asset(s) are moved to store successfully. <br/> <br/> Regards, <br/> info@honeycombmanager.com
                  `,
                );
              }
            }
            if (
              commonStore.staticDemoChanges &&
              typeof result?.data !== 'string'
            ) {
              if (
                DynamicDashboardStore.staticSelectedCard === 'Approval' &&
                updatedFormDetails.flatMap((data) =>
                  data.elementName === 'HOD Approval' ? data.elementValue : [],
                )?.[0] === 'Approved'
              ) {
                updateAssetInVault(result.data || [], 'IN TRANSIT');
              } else if (
                DynamicDashboardStore.staticSelectedCard ===
                  'Registrar Approval' &&
                updatedFormDetails.flatMap((data) =>
                  data.elementName === 'Registrar Approval'
                    ? data.elementValue
                    : [],
                )?.[0] === 'Denied'
              ) {
                updateAssetInVault(result.data || [], 'INSIDE');
              } else if (
                DynamicDashboardStore.staticSelectedCard === 'Ready to Deliver'
              ) {
                updateAssetInVault(result.data || [], 'OUTSIDE');
              } else if (
                DynamicDashboardStore.staticSelectedCard === 'Return'
              ) {
                updateAssetInVault(result.data || [], 'IN TRANSIT');
              } else if (
                DynamicDashboardStore.staticSelectedCard === 'Move to Store'
              ) {
                updateAssetInVault(result.data || [], 'INSIDE');
              }
            }
            modalStore.modal.open = false;
            modalStore.modal.body = null;
          }
        }
      } else {
        let payload = {
          assetId: assetIdLS,
          components: updatedFormDetails?.map((data) => {
            status += `***${data.elementName}*** : ~~~${data.elementValue}~~~ \n`;

            return {
              ...data,
              elementId: uuid(),
              options: data.options?.map((option: any) => ({
                ...option,
                optionId: uuid(),
              })),
            };
          }),
        };

        let [result] = await Promise.all([
          await agent.newMasterReport.postMetadataList(payload, id || ''),
          DynamicDashboardStore.assignUserMaster(
            [assetIdLS],
            userStore?.user?.id,
          ),
        ]);

        if (result?.data === errorMsg) {
          toast.warning(errorMsg);
        } else if (result?.data === `Asset not selected to scan`) {
          toast.warning(result.data);
        } else {
          getForm();
          setIsTableUpdated?.(true);
          toast.success('Form Saved Successfully');
          if (commonStore.staticDemoChanges) {
            let username = updatedFormDetails.flatMap((data) =>
              data.elementName === 'Student Name' ? data.elementValue : [],
            )?.[0];
            sendRespectiveEmail(
              '',
              '',
              // masterName || '',
              `${masterName} - Status update`,
              '',
              '',
              updatedFormDetails.flatMap((data) =>
                data.elementName === 'Student Email' ? data.elementValue : [],
              )?.[0],
              `Dear ${username},<br/>\u00A0 This is to notify that the ${masterName} is raised successfully. <br/> <br/> Regards, <br/> info@honeycombmanager.com
              `,
            );
          }
          // sendRespectiveEmail(
          //   'created',
          //   masterName || '',
          //   `${masterName} created successfully`,
          //   '',
          //   updatedFormDetails.flatMap((data) =>
          //     data.elementName === 'Student Email' ? data.elementValue : [],
          //   )?.[0],
          // );
          modalStore.modal.open = false;
          modalStore.modal.body = null;
        }
      }

      const assignUserList = updatedFormDetails?.filter(
        (ele) =>
          ele.elementType === 'Users' &&
          ele.elementValue?.split('|')?.[1] !== '',
      );
      const assignUserIDs: string[] = [];
      assignUserList?.forEach(async (ele: any) => {
        const names = ele.elementValue
          ?.split('|')?.[1]
          ?.split(',')
          .flatMap((d: string) => d || []);
        userList?.forEach((usr) => {
          if (names.includes(usr.displayName)) {
            assignUserIDs.push(usr.id);
          }
        });
      });

      if (assignUserIDs.length > 0) {
        DashboardConfigurationStore.assignUserMasterUpdate(
          assetIdLS,
          assignUserIDs,
        );
      }

      let history = {
        historyId: uuid(),
        assetId: assetIdLS,
        userId: user?.id || '',
        name: user?.displayName || '',
        department: user?.departmentName || '',
        imageUrl: user?.image || '',
        status,
        date: new Date().toISOString(),
      };
      await agent.newMasterReport.updateHistory(history);
      // ======== Communication Settings  =====
      await commSettingsForDynDash
        .showMaster(masterId as string)
        .then(async (Response) => {
          if (Response.data.length > 0) {
            await commSettingsForDynDash.showDynDashCommunication(
              assetIdLS,
              Response?.data[0]?.masterId,
            );
          }
        });

      setLoader((p) => ({
        ...p,
        submit: false,
      }));
    } catch {
      setLoader((p) => ({
        ...p,
        submit: false,
      }));
    }
  };

  const qrcode = <QRCodeCanvas id="qrCode" value={assetId || ''} />;

  return (
    <div className="new-master-report-form-wrapper">
      <div style={{ display: 'none' }} ref={qrRef}>
        {qrcode}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          borderBottom: '1px solid lightgrey',
        }}
      >
        <h4 style={{ marginBottom: '10px', color: '#3d87cc' }}>{`${
          isEdit ? `${masterName || 'Form'} ( Edit )` : formName || 'Form'
        }`}</h4>
        {loader.content && <ThreeDotLoader />}
      </div>
      {!loader.content &&
        (formDetails?.length === 0 ? (
          <h4
            style={{
              letterSpacing: '.5px',
              marginTop: '10px',
              color: 'grey',
            }}
          >
            {/* The form has been deleted. You will no longer be able to access this
            form or any of its submissions. */}
            Sorry, No data
          </h4>
        ) : (
          <FormComponentDyDb
            formDetails={formDetails}
            saveHandler={saveHandler}
            loader={loader}
            cancelBtnMode={cancelBtnMode}
            submitBtnMode={submitBtnMode}
            // submitBtnName={isEdit ? 'Edit' : undefined}
            cancelBtnName={isEdit ? 'Cancel' : undefined}
            assetId={assetId}
            dataGridName={dataGridName}
          />
        ))}
    </div>
  );
}

export default FormRegister;
